// src/constants/accessGuard.ts
var ACCESS_GUARD = {
  UNREGISTERED_COURSE_ACCESS: "unregisteredCourseAccess",
  UNREGISTERED_QA_ACCESS: "unregisteredQAAccess",
  UNREGISTERED_NEWS_ACCESS: "unregisteredNewsAccess",
  UNREGISTERED_ARTICLES_ACCESS: "unregisteredArticlesAccess"
};

// src/constants/aiMentorAvatar.ts
var ALLOWED_AVATAR_IMAGE_TYPES = ["image/jpeg", "image/png", "image/svg+xml", "image/jpg"];
var ALLOWED_EXTENSIONS = ".jpeg,.png,.svg,.jpg";

// src/constants/aiMentorTypes.ts
var AI_MENTOR_TYPE = {
  MENTOR: "mentor",
  TEACHER: "teacher",
  ROLEPLAY: "roleplay"
};

// src/constants/allowedAge.ts
var ALLOWED_AGE_LIMITS = [13, 16, null];

// src/constants/articlesSettings.ts
var ALLOWED_ARTICLES_SETTINGS = {
  ARTICLES_ENABLED: "articlesEnabled",
  UNREGISTERED_USER_ARTICLES_ACCESSIBILITY: "unregisteredUserArticlesAccessibility"
};

// src/constants/articlesTypes.ts
var ARTICLE_STATUS = {
  PUBLISHED: "published",
  DRAFT: "draft"
};

// src/constants/authorizationErrorCodes.ts
var AUTHORIZATION_ERROR_CODES = {
  /** Course has lessonSequenceEnabled and a previous lesson is not completed yet. */
  LESSON_SEQUENCE_LOCKED: "LESSON_SEQUENCE_LOCKED",
  /** Lesson belongs to a course the student has not purchased and is not freemium. */
  LESSON_NOT_PURCHASED: "LESSON_NOT_PURCHASED",
  /** Chapter belongs to a course the student has no access to and is not freemium. */
  CHAPTER_NOT_PURCHASED: "CHAPTER_NOT_PURCHASED",
  /** Endpoint requires the admin role. */
  ADMIN_ROLE_REQUIRED: "ADMIN_ROLE_REQUIRED"
};

// src/constants/certificateCode.ts
var CERTIFICATE_CODE_ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
var CERTIFICATE_CODE_LENGTH = 12;
var CERTIFICATE_CODE_GROUP_SIZE = 4;
var CERTIFICATE_CODE_SEPARATOR = "\xB7";
var CONFUSABLE_CHARS = {
  I: "1",
  L: "1",
  O: "0",
  U: "V"
  // Crockford descarta la U; quien la escribe casi siempre quiso una V.
};
function normalizeCertificateCode(input) {
  if (!input) return null;
  const stripped = input.toUpperCase().replace(/[^0-9A-Z]/g, "").split("").map((char) => CONFUSABLE_CHARS[char] ?? char).join("");
  if (stripped.length !== CERTIFICATE_CODE_LENGTH) return null;
  for (const char of stripped) {
    if (!CERTIFICATE_CODE_ALPHABET.includes(char)) return null;
  }
  return stripped;
}
function isValidCertificateCodeShape(input) {
  return normalizeCertificateCode(input) !== null;
}
function formatCertificateCode(code) {
  const canonical = normalizeCertificateCode(code);
  if (!canonical) return code ?? "";
  const groups = [];
  for (let i = 0; i < canonical.length; i += CERTIFICATE_CODE_GROUP_SIZE) {
    groups.push(canonical.slice(i, i + CERTIFICATE_CODE_GROUP_SIZE));
  }
  return groups.join(CERTIFICATE_CODE_SEPARATOR);
}

// src/constants/completionCriteria.ts
var COMPLETION_CRITERIA = {
  NONE: "none",
  SCROLL: "scroll",
  VIDEO: "video",
  SCROLL_AND_VIDEO: "scroll_and_video"
};

// src/constants/courseAccessLevel.ts
var COURSE_ACCESS_LEVEL = {
  FREE_PREVIEW: "free_preview",
  FULL: "full"
};
var COURSE_ACCESS_LEVELS = [
  COURSE_ACCESS_LEVEL.FREE_PREVIEW,
  COURSE_ACCESS_LEVEL.FULL
];
function isFullAccess(level) {
  return level === COURSE_ACCESS_LEVEL.FULL;
}
function canAccessChapter(level, chapter) {
  if (isFullAccess(level)) return true;
  return Boolean(chapter?.isFreemium);
}
function accessLevelForFreeAcquisition(isFree) {
  return isFree ? COURSE_ACCESS_LEVEL.FULL : COURSE_ACCESS_LEVEL.FREE_PREVIEW;
}

// src/constants/courseEnrollment.ts
var COURSE_ENROLLMENT = {
  ENROLLED: "enrolled",
  NOT_ENROLLED: "not_enrolled",
  GROUP_ENROLLED: "group_enrolled"
};

// src/constants/credentialType.ts
var CREDENTIAL_TYPE = {
  NONE: "none",
  CONSTANCIA: "constancia",
  CERTIFICADO: "certificado"
};
var CREDENTIAL_TYPES = [
  CREDENTIAL_TYPE.NONE,
  CREDENTIAL_TYPE.CONSTANCIA,
  CREDENTIAL_TYPE.CERTIFICADO
];
var ISSUABLE_CREDENTIAL_TYPES = [
  CREDENTIAL_TYPE.CONSTANCIA,
  CREDENTIAL_TYPE.CERTIFICADO
];
function issuesCredential(type) {
  return type === CREDENTIAL_TYPE.CONSTANCIA || type === CREDENTIAL_TYPE.CERTIFICADO;
}
var CREDENTIAL_LABEL = {
  [CREDENTIAL_TYPE.CONSTANCIA]: "Constancia de finalizaci\xF3n",
  [CREDENTIAL_TYPE.CERTIFICADO]: "Certificado"
};
var CREDENTIAL_LABEL_SHORT = {
  [CREDENTIAL_TYPE.CONSTANCIA]: "constancia",
  [CREDENTIAL_TYPE.CERTIFICADO]: "certificado"
};

// src/constants/entityTypes.ts
var ENTITY_TYPES = {
  COURSE: "course",
  CHAPTER: "chapter",
  LESSON: "lesson",
  QUESTION: "question",
  NEWS: "news",
  ARTICLES: "articles",
  USER: "user",
  CATEGORY: "category",
  ANNOUNCEMENT: "announcement",
  GLOBAL_SETTINGS: "global_settings"
};

// src/constants/fileTypes.ts
var ALLOWED_LESSON_IMAGE_FILE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/bmp",
  "image/tiff"
];
var ALLOWED_VIDEO_FILE_TYPES = [
  "video/mp4",
  "video/quicktime",
  "video/webm",
  "video/ogg",
  "video/avi",
  "video/mov",
  "video/wmv"
];
var ALLOWED_PRESENTATION_FILE_TYPES = [
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.oasis.opendocument.presentation"
];
var ALLOWED_EXCEL_FILE_TYPES = [
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/csv"
];
var ALLOWED_WORD_FILE_TYPES = [
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
];
var ALLOWED_PDF_FILE_TYPES = ["application/pdf"];

// src/constants/languages.ts
var SUPPORTED_LANGUAGES = {
  EN: "en",
  ES: "es"
};

// src/constants/lessonCompletionThresholds.ts
var VIDEO_END_TOLERANCE_SECONDS = 2;
var SCROLL_END_THRESHOLD = 0.985;
var EMBEDDED_VIDEO_END_FRACTION = 0.985;

// src/constants/lessonTypes.ts
var LESSON_TYPES = {
  CONTENT: "content",
  QUIZ: "quiz",
  AI_MENTOR: "ai_mentor",
  EMBED: "embed"
};

// src/constants/loginPageDocuments.ts
var MAX_LOGIN_PAGE_DOCUMENTS = 3;
var LOGIN_PAGE_DOCUMENTS_FILE_TYPES = ["application/pdf"];

// src/constants/newsSettings.ts
var ALLOWED_NEWS_SETTINGS = {
  NEWS_ENABLED: "newsEnabled",
  UNREGISTERED_USER_NEWS_ACCESSIBILITY: "unregisteredUserNewsAccessibility"
};

// src/constants/orderItemOutcome.ts
var ORDER_ITEM_OUTCOME = {
  /** Se le otorgó el acceso en esta orden. */
  ENROLLED: "enrolled",
  /** Ya tenía este acceso desde antes; la orden no cambió nada. */
  ALREADY_OWNED: "already_owned",
  /** Reserva de un curso que todavía no abrió (pre-inscripción). */
  PREINSCRIBED: "preinscribed",
  /** Tenía free preview y esta orden lo subió a full. */
  UPGRADED: "upgraded",
  /** Ítem pago todavía no cobrado: se entrega cuando el pago se aprueba. */
  PENDING_PAYMENT: "pending_payment",
  /** El acceso otorgado por esta orden fue revocado (reembolso / contracargo). */
  REVOKED: "revoked"
};
var ORDER_ITEM_OUTCOMES = [
  ORDER_ITEM_OUTCOME.ENROLLED,
  ORDER_ITEM_OUTCOME.ALREADY_OWNED,
  ORDER_ITEM_OUTCOME.PREINSCRIBED,
  ORDER_ITEM_OUTCOME.UPGRADED,
  ORDER_ITEM_OUTCOME.PENDING_PAYMENT,
  ORDER_ITEM_OUTCOME.REVOKED
];
var DELIVERED_OUTCOMES = [
  ORDER_ITEM_OUTCOME.ENROLLED,
  ORDER_ITEM_OUTCOME.UPGRADED
];
var ORDER_STATUS = {
  PENDING: "pending",
  AWAITING_PAYMENT: "awaiting_payment",
  /** La orden se creó pero no pudimos entregarle el link de pago por WhatsApp. */
  DELIVERY_FAILED: "delivery_failed",
  PROCESSING: "processing",
  COMPLETED: "completed",
  REJECTED: "rejected",
  CANCELLED: "cancelled",
  REFUNDED: "refunded",
  /**
   * Contracargo: el titular de la tarjeta desconoció el pago ante su banco. Es un estado
   * que reporta MercadoPago y que revoca el acceso igual que un reembolso, pero no es lo
   * mismo: acá la plata se va sin que la hayamos devuelto nosotros.
   */
  CHARGED_BACK: "charged_back"
};
var ORDER_STATUSES = [
  ORDER_STATUS.PENDING,
  ORDER_STATUS.AWAITING_PAYMENT,
  ORDER_STATUS.DELIVERY_FAILED,
  ORDER_STATUS.PROCESSING,
  ORDER_STATUS.COMPLETED,
  ORDER_STATUS.REJECTED,
  ORDER_STATUS.CANCELLED,
  ORDER_STATUS.REFUNDED,
  ORDER_STATUS.CHARGED_BACK
];
var ORDER_PAYABLE_STATUSES = [
  ORDER_STATUS.PENDING,
  ORDER_STATUS.AWAITING_PAYMENT,
  ORDER_STATUS.DELIVERY_FAILED,
  ORDER_STATUS.PROCESSING
];
function isOrderPayable(status) {
  return ORDER_PAYABLE_STATUSES.includes(status);
}

// src/constants/qaSettings.ts
var ALLOWED_QA_SETTINGS = {
  QA_ENABLED: "QAEnabled",
  UNREGISTERED_USER_QA_ACCESSIBILITY: "unregisteredUserQAAccessibility"
};

// src/constants/theme.ts
var HEX_COLOR_REGEX = /^#(?:[0-9a-fA-F]{3}){1,2}$/;

// src/constants/tus.ts
var DEFAULT_TUS_CHUNK_SIZE = 50 * 1024 * 1024;
var DEFAULT_TUS_TTL_MS = 4 * 60 * 60 * 1e3;

// src/constants/whatsappMessage.ts
var WHATSAPP_MESSAGE_KIND = {
  /** Código de verificación del login. Su contenido NUNCA se persiste en claro. */
  OTP: "otp",
  /** Link de pago de una orden. Es el único reenviable. */
  PAYMENT_LINK: "payment_link",
  /** Mensaje de texto libre (fuera de template). */
  TEXT: "text"
};
var WHATSAPP_MESSAGE_KINDS = [
  WHATSAPP_MESSAGE_KIND.OTP,
  WHATSAPP_MESSAGE_KIND.PAYMENT_LINK,
  WHATSAPP_MESSAGE_KIND.TEXT
];
var WHATSAPP_MESSAGE_STATUS = {
  /** Fila escrita antes de disparar el request; todavía no sabemos nada. */
  PENDING: "pending",
  /** Kapso aceptó el envío y nos devolvió un wamid. */
  SENT: "sent",
  /** Meta confirmó la entrega en el dispositivo. */
  DELIVERED: "delivered",
  /** El destinatario abrió el mensaje. */
  READ: "read",
  /** Meta lo rechazó o no pudo entregarlo (trae código de error). */
  FAILED: "failed",
  /** El request a Kapso falló de nuestro lado (red, credenciales, 4xx/5xx). */
  ERROR: "error"
};
var WHATSAPP_MESSAGE_STATUSES = [
  WHATSAPP_MESSAGE_STATUS.PENDING,
  WHATSAPP_MESSAGE_STATUS.SENT,
  WHATSAPP_MESSAGE_STATUS.DELIVERED,
  WHATSAPP_MESSAGE_STATUS.READ,
  WHATSAPP_MESSAGE_STATUS.FAILED,
  WHATSAPP_MESSAGE_STATUS.ERROR
];
var WHATSAPP_STATUS_RANK = {
  [WHATSAPP_MESSAGE_STATUS.PENDING]: 0,
  [WHATSAPP_MESSAGE_STATUS.ERROR]: 1,
  [WHATSAPP_MESSAGE_STATUS.SENT]: 2,
  [WHATSAPP_MESSAGE_STATUS.DELIVERED]: 3,
  [WHATSAPP_MESSAGE_STATUS.READ]: 4,
  /**
   * `failed` gana sobre todo: si Meta terminó reportando una falla, eso es lo que hay que
   * mostrarle a soporte aunque antes hubiera pasado por `sent`.
   */
  [WHATSAPP_MESSAGE_STATUS.FAILED]: 5
};
function isWhatsAppStatusAdvance(current, next) {
  const currentRank = WHATSAPP_STATUS_RANK[current] ?? -1;
  return WHATSAPP_STATUS_RANK[next] > currentRank;
}
var WEBHOOK_PROVIDER = {
  KAPSO: "kapso",
  MERCADOPAGO: "mercadopago"
};

// src/types/onboarding.ts
var OnboardingPages = /* @__PURE__ */ ((OnboardingPages2) => {
  OnboardingPages2["DASHBOARD"] = "dashboard";
  OnboardingPages2["COURSES"] = "courses";
  OnboardingPages2["ANNOUNCEMENTS"] = "announcements";
  OnboardingPages2["PROFILE"] = "profile";
  OnboardingPages2["SETTINGS"] = "settings";
  OnboardingPages2["PROVIDER_INFORMATION"] = "providerInformation";
  return OnboardingPages2;
})(OnboardingPages || {});

// src/types/videoUploadTypes.ts
var VIDEO_UPLOAD_STATUS = {
  QUEUED: "queued",
  UPLOADED: "uploaded",
  PROCESSED: "processed",
  FAILED: "failed"
};
var VIDEO_PROVIDERS = {
  BUNNY: "bunny",
  S3: "s3"
};

// src/utils/coursePricing.ts
function isCourseFree(course) {
  if (!course) return false;
  return (course.priceInCents ?? 0) === 0 && !course.mercadopagoProductId;
}
function isCourseReservation(course) {
  return course?.isPreinscription === true;
}
function isCoursePaid(course) {
  return !isCourseFree(course);
}
function courseChargeableCents(course, currency) {
  if (!course || isCourseFree(course) || isCourseReservation(course)) return 0;
  const useMercadoPago = (currency ?? "").toUpperCase() === "ARS";
  const mp = course.mercadopagoPriceInCents ?? 0;
  const base = course.priceInCents ?? 0;
  return useMercadoPago && mp > 0 ? mp : base;
}
function cartChargeableCents(items, currency) {
  if (!items?.length) return 0;
  return items.reduce((sum, item) => sum + courseChargeableCents(item, currency), 0);
}
function splitByPricing(items) {
  const free = [];
  const paid = [];
  for (const item of items ?? []) {
    (isCourseFree(item) ? free : paid).push(item);
  }
  return { free, paid };
}
function splitByCheckoutKind(items) {
  const reservations = [];
  const free = [];
  const paid = [];
  for (const item of items ?? []) {
    if (isCourseReservation(item)) reservations.push(item);
    else if (isCourseFree(item)) free.push(item);
    else paid.push(item);
  }
  return { reservations, free, paid };
}

// src/utils/lessonCompletionGate.ts
var CONTENT = "content";
var EMBED = "embed";
function usableMediaUrl(value) {
  if (typeof value !== "string") return null;
  const url = value.trim();
  if (!url || url === "null" || url === "undefined") return null;
  if (!/^(https?:\/\/|\/\/|\/)/i.test(url)) return null;
  return url;
}
function stripEmbeddedVideos(html) {
  return html.replace(/<figure\b[^>]*>[\s\S]*?<video\b[\s\S]*?<\/video>[\s\S]*?<\/figure>/gi, "").replace(/<video\b[\s\S]*?<\/video>/gi, "");
}
function hasVideoTag(html) {
  return /<video[\s>]/i.test(html);
}
function hasReadableMaterial(html) {
  const withoutVideos = stripEmbeddedVideos(html).replace(/<style[^>]*>[\s\S]*?<\/style>/gi, "").replace(/<script[^>]*>[\s\S]*?<\/script>/gi, "").replace(/<svg[^>]*>[\s\S]*?<\/svg>/gi, "");
  const visibleText = withoutVideos.replace(/<[^>]*>/g, " ").replace(/&nbsp;|&#160;|&#xA0;/gi, " ").replace(/\s+/g, " ").trim();
  return visibleText.length > 0 || /<(img|table|iframe|audio|canvas)\b/i.test(withoutVideos);
}
function resolveLessonCompletionGate(lesson, options = {}) {
  const type = lesson.type ?? CONTENT;
  const description = lesson.description ?? "";
  const videoWebUrl = usableMediaUrl(lesson.videoWebUrl);
  const fileVideoUrl = lesson.fileType === "video" ? usableMediaUrl(lesson.fileUrl ?? void 0) : null;
  const videoFirst = type === CONTENT && Boolean(videoWebUrl);
  const embeddedVideo = options.canObserveEmbeddedVideo ?? (hasVideoTag(description) && type === CONTENT);
  const hasVideo = Boolean(
    videoFirst || fileVideoUrl || embeddedVideo || lesson.hasVideo || lesson.hasOnlyVideo
  );
  const gated = type === CONTENT || type === EMBED;
  if (!gated) {
    return {
      hasVideo,
      videoFirst: false,
      requiresVideo: false,
      requiresReading: false,
      readingLocation: "page",
      noRequirements: false,
      completesOnVisit: false
    };
  }
  const requiresVideo = Boolean(videoFirst || fileVideoUrl || embeddedVideo && type === CONTENT);
  const explicitlyNoRequirements = lesson.completionCriteria === "none";
  const readableMaterial = hasReadableMaterial(description);
  const requiresReading = !requiresVideo && !explicitlyNoRequirements && readableMaterial;
  const noRequirements = !requiresVideo && !requiresReading;
  return {
    hasVideo,
    videoFirst,
    requiresVideo,
    requiresReading,
    // Mandar al alumno a un panel que no está en pantalla fue el bug original:
    // el panel "Material requerido" SÓLO existe en el layout video-first.
    readingLocation: videoFirst ? "panel" : "page",
    noRequirements,
    // NUNCA se completa una clase por el solo hecho de abrirla.
    //
    // Esto valía `!requiresVideo && !requiresReading` con una idea razonable —si no hay
    // nada verificable, no encerremos al alumno— y el resultado en la práctica fue
    // peor que el problema que resolvía: clases que se daban por vistas con apretar
    // play, y las siguientes autocompletándose en cadena mientras el alumno mira.
    //
    // Entre una clase que pide una acción de más y una que se regala, preferimos la
    // primera: perder una clase que el alumno hizo es recuperable, darle por vista una
    // que no vio no lo es. Una clase sin nada verificable es un DATO ROTO y se arregla
    // en el contenido (hay un validador de salud de curso para detectarlas), no
    // dándola por cumplida.
    completesOnVisit: false
  };
}

// src/utils/videoProgress.ts
var RANGE_JOIN_TOLERANCE_SECONDS = 0.25;
function mergeVideoWatchedRanges(...rangeSets) {
  const duration = rangeSets.reduce((current, set) => {
    const candidate = Number(set.durationSeconds);
    if (!Number.isFinite(candidate) || candidate <= 0) return current;
    return current === void 0 ? candidate : Math.min(current, candidate);
  }, void 0);
  const sanitized = rangeSets.flatMap((set) => set.ranges ?? []).map(([rawStart, rawEnd]) => {
    const start = Math.max(0, Number(rawStart));
    const end = duration === void 0 ? Number(rawEnd) : Math.min(duration, Number(rawEnd));
    return [start, end];
  }).filter(
    ([start, end]) => Number.isFinite(start) && Number.isFinite(end) && end > start
  ).sort(([startA, endA], [startB, endB]) => startA - startB || endA - endB);
  const merged = [];
  for (const [start, end] of sanitized) {
    const previous = merged.at(-1);
    if (!previous || start > previous[1] + RANGE_JOIN_TOLERANCE_SECONDS) {
      merged.push([start, end]);
      continue;
    }
    previous[1] = Math.max(previous[1], end);
  }
  return merged;
}
function videoWatchedSeconds(ranges) {
  return mergeVideoWatchedRanges({ ranges }).reduce(
    (total, [start, end]) => total + end - start,
    0
  );
}
export {
  ACCESS_GUARD,
  AI_MENTOR_TYPE,
  ALLOWED_AGE_LIMITS,
  ALLOWED_ARTICLES_SETTINGS,
  ALLOWED_AVATAR_IMAGE_TYPES,
  ALLOWED_EXCEL_FILE_TYPES,
  ALLOWED_EXTENSIONS,
  ALLOWED_LESSON_IMAGE_FILE_TYPES,
  ALLOWED_NEWS_SETTINGS,
  ALLOWED_PDF_FILE_TYPES,
  ALLOWED_PRESENTATION_FILE_TYPES,
  ALLOWED_QA_SETTINGS,
  ALLOWED_VIDEO_FILE_TYPES,
  ALLOWED_WORD_FILE_TYPES,
  ARTICLE_STATUS,
  AUTHORIZATION_ERROR_CODES,
  CERTIFICATE_CODE_ALPHABET,
  CERTIFICATE_CODE_GROUP_SIZE,
  CERTIFICATE_CODE_LENGTH,
  CERTIFICATE_CODE_SEPARATOR,
  COMPLETION_CRITERIA,
  COURSE_ACCESS_LEVEL,
  COURSE_ACCESS_LEVELS,
  COURSE_ENROLLMENT,
  CREDENTIAL_LABEL,
  CREDENTIAL_LABEL_SHORT,
  CREDENTIAL_TYPE,
  CREDENTIAL_TYPES,
  DEFAULT_TUS_CHUNK_SIZE,
  DEFAULT_TUS_TTL_MS,
  DELIVERED_OUTCOMES,
  EMBEDDED_VIDEO_END_FRACTION,
  ENTITY_TYPES,
  HEX_COLOR_REGEX,
  ISSUABLE_CREDENTIAL_TYPES,
  LESSON_TYPES,
  LOGIN_PAGE_DOCUMENTS_FILE_TYPES,
  MAX_LOGIN_PAGE_DOCUMENTS,
  ORDER_ITEM_OUTCOME,
  ORDER_ITEM_OUTCOMES,
  ORDER_PAYABLE_STATUSES,
  ORDER_STATUS,
  ORDER_STATUSES,
  OnboardingPages,
  SCROLL_END_THRESHOLD,
  SUPPORTED_LANGUAGES,
  VIDEO_END_TOLERANCE_SECONDS,
  VIDEO_PROVIDERS,
  VIDEO_UPLOAD_STATUS,
  WEBHOOK_PROVIDER,
  WHATSAPP_MESSAGE_KIND,
  WHATSAPP_MESSAGE_KINDS,
  WHATSAPP_MESSAGE_STATUS,
  WHATSAPP_MESSAGE_STATUSES,
  accessLevelForFreeAcquisition,
  canAccessChapter,
  cartChargeableCents,
  courseChargeableCents,
  formatCertificateCode,
  isCourseFree,
  isCoursePaid,
  isCourseReservation,
  isFullAccess,
  isOrderPayable,
  isValidCertificateCodeShape,
  isWhatsAppStatusAdvance,
  issuesCredential,
  mergeVideoWatchedRanges,
  normalizeCertificateCode,
  resolveLessonCompletionGate,
  splitByCheckoutKind,
  splitByPricing,
  stripEmbeddedVideos,
  videoWatchedSeconds
};
